import { useCallback, useState, useEffect, useMemo, useRef } from "react";
import Map, { Source, Layer, NavigationControl, Marker } from "react-map-gl";
import {
  mapConfig,
  mapStyle,
  mapStyles,
  mapboxToken,
  stateCoordinates,
} from "../../config";
import { abbrState, filterSchools } from "../../utils";
import states from "../../data/states";
import SchoolPopup from "../../components/SchoolPopup";
import MapButtons from "../MapButtons";
import schoolsData from "../../data/schools";
import * as layers from "../../layers";
import { AppFilters, MapFiltersButton, StateMarker } from "./styled.js";
import { Wrapper, Container, MapSearch } from "../../components/App/styled.js";

function SchoolsMap({ className, ...props }) {
  const isMobile = window.matchMedia("(max-width: 640px)").matches;
  const schoolMapConfig = isMobile
    ? { ...mapConfig, zoom: 2, minZoom: 2 }
    : mapConfig;
  const [caseStudy, setCaseStudy] = useState(null);
  const [school, setSchool] = useState(null);
  const [schools, setSchools] = useState(schoolsData);
  const [stateTotals, setStateTotals] = useState([]);
  const [mapViewState, setMapViewState] = useState(schoolMapConfig);
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [activeFilters, setActiveFilters] = useState({
    systemSize: [],
    funding: [],
    structureType: [],
    caseStudies: [],
    batteryStorage: [],
    cleanEnergyTechnology: []
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [activeSchoolsCircle, setActiveSchoolsCircle] = useState("");
  const [activeClusterLayer1, setActiveClusterLayer1] = useState("");
  const [activeClusterLayer2, setActiveClusterLayer2] = useState("");
  const [activeClusterLayer3, setActiveClusterLayer3] = useState("");
  const [activeClusterLayer4, setActiveClusterLayer4] = useState("");

  const mapRef = useRef();

  const handleStateCountClick = (event) => {
    mapRef.current.flyTo({
      center: [event.target._lngLat.lng, event.target._lngLat.lat],
      duration: 1000,
      zoom: 5,
    });
  };

  useEffect(() => {
    let stateTotals = [];
    if (schools == null) return;
    schools.forEach(function (x) {
      if (stateTotals[x.properties.state] == null) {
        stateTotals[x.properties.state] = 1;
      } else {
        stateTotals[x.properties.state]++;
      }
    });
    setStateTotals(stateTotals);
  }, [schools]);

  const stateCountMarkers = useMemo(
    () =>
      states.features.map((x, index) => {
        const coordinates = stateCoordinates.find((y) => {
          return y[0] === x.properties.name;
        });
        x.properties.longitude = coordinates[7];
        x.properties.latitude = coordinates[6];
        let color = mapStyles.stateCountTier3Color;
        
        if (parseInt(x.properties.number_solar_schools) < 10) {
          color = mapStyles.stateCountTier1Color;
        } else if (parseInt(x.properties.number_solar_schools) < 50) {
          color = mapStyles.stateCountTier2Color;
        } else if (parseInt(x.properties.number_solar_schools) < 100) {
          color = mapStyles.stateCountTier3Color;
        } else if (parseInt(x.properties.number_solar_schools) < 501) {
          color = mapStyles.stateCountTier4Color;
        } else {
          color = mapStyles.stateCountTier5Color;
        }
        if (!stateTotals[abbrState(x.properties.name)]) {
          return null;
        }
        return (
          <Marker
            key={`marker-${index}`}
            longitude={x.properties.longitude}
            latitude={x.properties.latitude}
            onClick={handleStateCountClick}
            anchor="bottom"
          >
            <StateMarker color={color}>
              {stateTotals[abbrState(x.properties.name)]}
            </StateMarker>
          </Marker>
        );
      }),
    [stateTotals]
  );

  const onClick = useCallback((event) => {
    const feature = event.features && event.features[0];

    switch (feature.layer.id) {
      case "schools-circle":
        mapRef.current.flyTo({
          center: [event.lngLat.lng, event.lngLat.lat],
          duration: 1000,
          zoom: 8,
        });
        break;
      case "schools-cluster":
        mapRef.current.flyTo({
          center: [event.lngLat.lng, event.lngLat.lat],
          duration: 1000,
          zoom: 8,
        });
        break;
      case "schools-marker":
        if (!feature.properties.cluster) {
          setSchool(feature.properties);
          setCaseStudy(null);
          mapRef.current.flyTo({
            center: [feature.properties.longitude, feature.properties.latitude],
            duration: 1000,
            zoom: 8,
          });
        }
        break;
      case "case-studies-marker":
        if (!feature.properties.cluster) {
          setCaseStudy(feature.properties);
          setSchool(null);
          mapRef.current.flyTo({
            center: [feature.properties.longitude, feature.properties.latitude],
            duration: 1000,
            zoom: 8,
          });
        }
        break;
      default:
        break;
    }
  }, []);

  const onMouseEnter = useCallback((event) => {
    const feature = event.features && event.features[0];
    switch (feature.layer.id) {
      case "schools-marker":
        break;
      case "case-studies-marker":
        break;
      case "schools-circle":
        setActiveSchoolsCircle(feature.properties.id);
        break;
      default:
        break;
    }
    if (feature?.layer.id === "schools-cluster") {
      const pointCount = feature.properties.point_count;
      if (pointCount <= 50) {
        setActiveClusterLayer1(feature.properties.cluster_id);
      } else if (pointCount <= 99) {
        setActiveClusterLayer2(feature.properties.cluster_id);
      } else if (pointCount <= 249) {
        setActiveClusterLayer3(feature.properties.cluster_id);
      } else {
        setActiveClusterLayer4(feature.properties.cluster_id);
      }
    }
  }, []);

  const onMouseLeave = useCallback((event) => {
    setActiveSchoolsCircle("");
    setActiveClusterLayer1("");
    setActiveClusterLayer2("");
    setActiveClusterLayer3("");
    setActiveClusterLayer4("");
  }, []);

  const handleToggle = () => {
    setFiltersOpen(!filtersOpen);
  };

  const handleFilter = (filters) => {
    setActiveFilters(filters);
  };

  const handleSearch = (searchTerm) => {
    setActiveSearchTerm(searchTerm);
  };

  // useEffect(() => {
  //   setSchools(filterSchools(activeSearchTerm, activeFilters, schoolsData));
  //   setSchool(null);
  //   setCaseStudy(null);
  // }, []);

  useEffect(() => {
    setSchools(filterSchools(activeSearchTerm, activeFilters, schoolsData));
  }, [activeSearchTerm, activeFilters]);

  return (
    <>
      <MapFiltersButton activeFilters={activeFilters} results={schools} onClick={handleToggle} />
      <Wrapper>
        <MapSearch
          placeholder="School Name, District, City"
          onSearch={handleSearch}
        />
        <Container>
          <AppFilters
            activeFilters={activeFilters}
            results={schools}
            isOpen={filtersOpen}
            onToggle={handleToggle}
            onFilter={handleFilter}
          />
          <Map
            {...mapViewState}
            id="schoolsMap"
            ref={mapRef}
            onMove={(evt) => setMapViewState(evt.viewState)}
            width="100%"
            height="100%"
            mapStyle={mapStyle}
            mapboxAccessToken={mapboxToken}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            interactiveLayerIds={[
              "schools-marker",
              // "case-studies-marker",
              "schools-circle",
              "schools-cluster",
            ]}
          >
            <NavigationControl showCompass={false} />
            <Source id="states" type="geojson" data={states}>
              <Layer {...layers.stateBordersSchoolsLayer} />
            </Source>
            <Source
              id="schools-data"
              type="geojson"
              data={{ type: "FeatureCollection", features: schools }}
              cluster={true}
              clusterMaxZoom={6}
              clusterRadius={50}
            >
              <Layer {...layers.schoolsMarkerLayer} />
              {/* <Layer {...layers.caseStudiesMarkerLayer} /> */}
              <Layer {...layers.schoolsCircleLayer} />
              <Layer
                {...layers.schoolsCircleHoverLayer}
                filter={["==", "id", activeSchoolsCircle]}
              />
              <Layer {...layers.schoolsClusterLayer} />
              <Layer
                {...layers.clustersHoverLayer1}
                filter={["==", "cluster_id", activeClusterLayer1]}
              />
              <Layer
                {...layers.clustersHoverLayer2}
                filter={["==", "cluster_id", activeClusterLayer2]}
              />
              <Layer
                {...layers.clustersHoverLayer3}
                filter={["==", "cluster_id", activeClusterLayer3]}
              />
              <Layer
                {...layers.clustersHoverLayer4}
                filter={["==", "cluster_id", activeClusterLayer4]}
              />
              <Layer {...layers.schoolsClusterCountLayer} />
              {mapViewState.zoom < 5 && stateCountMarkers}
            </Source>
            {caseStudy && (
              <SchoolPopup
                anchor="bottom"
                longitude={Number(caseStudy.longitude)}
                latitude={Number(caseStudy.latitude)}
                onClose={() => setCaseStudy(null)}
                closeOnClick={false}
                data={caseStudy}
              ></SchoolPopup>
            )}
            {school && (
              <SchoolPopup
                anchor="bottom"
                longitude={Number(school.longitude)}
                latitude={Number(school.latitude)}
                onClose={() => setSchool(null)}
                closeOnClick={false}
                data={school}
              ></SchoolPopup>
            )}
          </Map>
          <MapButtons mapRef={mapRef} showStates={true} />
        </Container>
      </Wrapper>
    </>
  );
}

export default SchoolsMap;
